import { NavLink, useLocation } from "react-router-dom";
import { Heart, Home, Image, Target, BookOpen, Menu, X, CheckSquare, User, LogOut } from "lucide-react";
import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/contexts/AuthContext";
import NotificationBell from "@/components/NotificationBell";

const navItems = [
  { to: "/app", icon: Home, label: "Home" },
  { to: "/app/vision", icon: Image, label: "Vision Board" },
  { to: "/app/goals", icon: Target, label: "Our Goals" },
  { to: "/app/reflect", icon: BookOpen, label: "Board Meeting" },
  { to: "/app/todos", icon: CheckSquare, label: "To-Dos" },
];

export default function AppLayout({ children }: { children: React.ReactNode }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { profile, workspace, signOut } = useAuth();

  const displayName = profile?.preferred_name || profile?.full_name || "You";

  return (
    <div className="flex min-h-screen bg-background overflow-x-hidden">
      {/* Desktop Sidebar */}
      <aside className="hidden md:flex w-64 flex-col border-r border-border bg-sidebar p-6 fixed h-full">
        <div className="flex items-center gap-2 mb-2">
          <Heart className="w-5 h-5 text-accent flex-shrink-0" fill="currentColor" />
          <span className="font-display text-xl font-semibold text-foreground tracking-tight truncate">Together OS</span>
        </div>
        {workspace && <p className="text-xs text-muted-foreground mb-8 ml-7 truncate">{workspace.couple_name}</p>}

        <nav className="flex flex-col gap-1 flex-1">
          {navItems.map((item) => (
            <NavLink key={item.to} to={item.to} end={item.to === "/app"} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
              <item.icon className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="mt-auto pt-4 border-t border-border space-y-3">
          <div className="flex items-center justify-between px-4">
            <NavLink to="/app/profile" className={({ isActive }) => `flex items-center gap-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${isActive ? "text-primary" : "text-muted-foreground hover:text-foreground"}`}>
              <User className="w-4 h-4 flex-shrink-0" />
              <span className="truncate">{displayName}</span>
            </NavLink>
            <NotificationBell />
          </div>
          <button onClick={signOut} className="flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors w-full">
            <LogOut className="w-4 h-4 flex-shrink-0" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2 min-w-0">
          <Heart className="w-4 h-4 text-accent flex-shrink-0" fill="currentColor" />
          <span className="font-display text-lg font-semibold truncate">{workspace?.couple_name || "Together OS"}</span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <NotificationBell />
          <button onClick={() => setMobileOpen(!mobileOpen)} className="p-2 text-foreground">
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Nav Overlay */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="md:hidden fixed inset-x-0 top-14 z-40 bg-background border-b border-border p-4"
          >
            <nav className="flex flex-col gap-1">
              {navItems.map((item) => (
                <NavLink key={item.to} to={item.to} end={item.to === "/app"} onClick={() => setMobileOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                  <item.icon className="w-4 h-4 flex-shrink-0" />
                  {item.label}
                </NavLink>
              ))}
              <NavLink to="/app/profile" onClick={() => setMobileOpen(false)} className={({ isActive }) => `flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-secondary hover:text-foreground"}`}>
                <User className="w-4 h-4 flex-shrink-0" />
                Profile
              </NavLink>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 pt-16 md:pt-0 overflow-x-hidden">
        <div className="max-w-5xl mx-auto px-4 md:px-8 py-8 md:py-12">
          {children}
        </div>
      </main>
    </div>
  );
}
